import React, { useState, useRef } from 'react';
import Button from '../../components/ui/Button';
import InputField from '../../components/ui/InputField';
import Card from '../../components/ui/Card';
import PlayArrowIcon from '../../components/icons/PlayArrowIcon';
import TrashIcon from '../../components/icons/TrashIcon';
import RedoIcon from '../../components/icons/RedoIcon';

const NumberDrawer: React.FC = () => {
  const [minNumber, setMinNumber] = useState<number>(1);
  const [maxNumber, setMaxNumber] = useState<number>(45);
  const [drawnNumbers, setDrawnNumbers] = useState<number[]>([]);
  const [isDrawing, setIsDrawing] = useState<boolean>(false);
  const [currentNumber, setCurrentNumber] = useState<number | null>(null);
  const [drawSpeed, setDrawSpeed] = useState<number>(50);
  const drawingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const startDrawing = () => {
    if (isDrawing) return;
    const availableNumbers = Array.from(
      { length: maxNumber - minNumber + 1 },
      (_, i) => i + minNumber
    ).filter(num => !drawnNumbers.includes(num));
    if (availableNumbers.length === 0) {
      alert('모든 번호가 추첨되었습니다.');
      return;
    }
    setIsDrawing(true);
    drawingIntervalRef.current = setInterval(() => {
      const randomIndex = Math.floor(Math.random() * availableNumbers.length);
      setCurrentNumber(availableNumbers[randomIndex]);
    }, drawSpeed);
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    if (drawingIntervalRef.current) {
      clearInterval(drawingIntervalRef.current);
      drawingIntervalRef.current = null;
    }
    if (currentNumber !== null) {
      setDrawnNumbers(prev => [...prev, currentNumber]);
    }
    setIsDrawing(false);
    setCurrentNumber(null);
  };

  const resetDrawing = () => {
    if (drawingIntervalRef.current) {
      clearInterval(drawingIntervalRef.current);
      drawingIntervalRef.current = null;
    }
    setDrawnNumbers([]);
    setCurrentNumber(null);
    setIsDrawing(false);
  };

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: 24 }}>
      <h1 style={{ textAlign: 'center', marginBottom: 24, fontSize: 24 }}>
        번호 추첨기
      </h1>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <div style={{ flex: 1 }}>
          <div style={{ marginBottom: 24 }}>
            <p style={{ marginBottom: 8 }}>번호 범위 설정</p>
            <div style={{ display: 'flex', gap: 16 }}>
              <InputField
                label="최소 번호"
                type="number"
                value={String(minNumber)}
                onChange={(v) => setMinNumber(Math.max(1, parseInt(v) || 1))}
              />
              <InputField
                label="최대 번호"
                type="number"
                value={String(maxNumber)}
                onChange={(v) => setMaxNumber(Math.max(minNumber, parseInt(v) || minNumber))}
              />
            </div>
          </div>
          <div style={{ marginBottom: 24 }}>
            <p style={{ marginBottom: 8 }}>추첨 속도 (ms)</p>
            <input
              type="range"
              min={10}
              max={200}
              step={10}
              value={drawSpeed}
              onChange={(e) => setDrawSpeed(Number(e.target.value))}
              style={{ width: '100%' }}
            />
            <span style={{ fontSize: 14, color: '#666' }}>{drawSpeed}ms</span>
          </div>
          <div style={{ display: 'flex', gap: 16 }}>
            <Button
              variant="primary"
              onClick={isDrawing ? stopDrawing : startDrawing}
              leftIcon={isDrawing ? <TrashIcon width={20} height={20} /> : <PlayArrowIcon width={20} height={20} />}
              style={{ flex: 1 }}
            >
              {isDrawing ? '정지' : '추첨 시작'}
            </Button>
            <Button variant="outline" onClick={resetDrawing} leftIcon={<RedoIcon width={20} height={20} />} style={{ flex: 1 }}>
              초기화
            </Button>
          </div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <div style={{
              fontSize: '4rem',
              fontWeight: 'bold',
              minHeight: 100,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--Color-fill-brand-color, #FF6F0F)',
            }}>
              {currentNumber ?? '-'}
            </div>
          </div>
          <div>
            <h2 style={{ marginBottom: 12, fontSize: 18 }}>추첨된 번호</h2>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8,
              padding: 16,
              backgroundColor: 'var(--Color-background-normal)',
              borderRadius: 8,
            }}>
              {drawnNumbers.slice().sort((a, b) => a - b).map((num) => (
                <Card key={num} padding="8px 12px" style={{ minWidth: 40, textAlign: 'center', background: 'var(--Color-fill-brand-color)', color: '#fff' }}>
                  {num}
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NumberDrawer;
